import { useState, useEffect, useCallback } from 'react';
import { ItemsAPI } from '../../api/payments/items/api';
import {
  buildItemsQueryParams,
  filterItemsByText,
  sortByNewestFirst,
} from './utils';

export function useItemsBootstrap() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

  const fetchItems = useCallback(
    async (page, customPerPage = perPage) => {
      setLoading(true);
      setErrorMessage('');

      try {
        const apiPage = page;
        const normalizedFilterText = filterText.trim();
        const requestLimit = normalizedFilterText
          ? Math.max(customPerPage, 1000)
          : customPerPage;

        const params = buildItemsQueryParams({
          page: apiPage,
          limit: requestLimit,
          filterText: normalizedFilterText,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        });

        const response = await ItemsAPI.list(params);

        if (response.data && response.data.success) {
          const responsePayload = response.data.data;

          const itemsList = Array.isArray(responsePayload)
            ? responsePayload
            : Array.isArray(responsePayload?.data)
            ? responsePayload.data
            : Array.isArray(response.data?.content)
            ? response.data.content
            : [];

          const sortedItems = sortByNewestFirst(itemsList);
          const filteredItems = normalizedFilterText
            ? filterItemsByText(sortedItems, normalizedFilterText)
            : sortedItems;

          const startIndex = (apiPage - 1) * customPerPage;
          const paginatedItems = filteredItems.slice(
            startIndex,
            startIndex + customPerPage,
          );

          setData(paginatedItems);

          const resolvedTotal =
            response.data.total ||
            responsePayload?.totalItems ||
            responsePayload?.total ||
            response.data?.totalElements ||
            filteredItems.length ||
            0;

          setTotalRows(
            normalizedFilterText ? filteredItems.length : resolvedTotal,
          );
        } else {
          setData([]);
          setTotalRows(0);
        }
      } catch (error) {
        setErrorMessage(
          'Nao foi possivel carregar os produtos. Tente novamente.',
        );

        setData([]);
        setTotalRows(0);

        console.error('Erro ao buscar itens:', error);
      } finally {
        setLoading(false);
      }
    },
    [perPage, filterText],
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchItems(page);
  };

  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    setResetPaginationToggle((previous) => !previous);
    fetchItems(1, newPerPage);
  };

  const handleFilterChange = (event) => {
    setFilterText(event.target.value);
  };

  useEffect(() => {
    setCurrentPage(1);
    fetchItems(1);
  }, [filterText, fetchItems]);

  return {
    data,
    loading,
    errorMessage,
    totalRows,
    perPage,
    currentPage,
    filterText,
    resetPaginationToggle,
    handlePageChange,
    handlePerRowsChange,
    handleFilterChange,
  };
}
